import { useFormattedOfferedMaxTerms } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useFormattedOfferedMaxTerms";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useRetailerFinancial } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerFinancial";
import { SignUpTitle } from "@faire/retailer-visitor-shared/views/SignUp/_components/SignUpTitle";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { RoundingStrategy, moneyOrZero } from "@faire/web--source/common/money";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Money } from "@faire/web--source/ui/Money";
import React, { useMemo } from "react";

import { useRetailerUpmarket } from "../../_hooks/useRetailerUpmarket";

interface IProps {
  isUpmarketQualified: boolean;
  isTreatmentForSccLimitIncreaseExperiment: boolean;
  isRetailerEligibleForOwF: boolean;
}

export const Headers: React.FC<IProps> = ({
  isUpmarketQualified,
  isTreatmentForSccLimitIncreaseExperiment,
  isRetailerEligibleForOwF,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { retailer } = useRetailer();
  const { currency: displayCurrency } = useRetailerFinancial();
  const formattedOfferedMaxTerms = useFormattedOfferedMaxTerms();
  const hasSkippedOwFUpsell = useRetailerUpmarket();

  const { title, subtitle } = useMemo(() => {
    if (isUpmarketQualified) {
      return {
        title: (
          <StrictLocalMsg
            defaultMessage="You’ve got {netTermsMoney} in initial payment terms"
            description={{
              text: "Header title letting retailer know they have payment terms",
              img: "https://cdn.faire.com/fastly/cccce764b65af9b1b6adfc0b8cb9fb9fe9818de5896c2f9fa31d57c605903825.png",
            }}
            values={{
              netTermsMoney: (
                <Money
                  money={moneyOrZero(retailer?.terms_limit, displayCurrency)}
                  roundingStrategy={RoundingStrategy.DROP_CENTS}
                />
              ),
            }}
          />
        ),
        subtitle: isTreatmentForSccLimitIncreaseExperiment
          ? strictLocalize({
              defaultMessage:
                "Faire offers higher terms to qualified retailers.",
              description: {
                text: "Header subtitle explaining higher payment terms are available to qualifying retailers",
                img: "https://cdn.faire.com/fastly/cccce764b65af9b1b6adfc0b8cb9fb9fe9818de5896c2f9fa31d57c605903825.png",
              },
            })
          : strictLocalize({
              defaultMessage:
                "Faire offers higher terms to qualified retailers. You can request more once you’ve created your account.",
              description: {
                text: "Header subtitle explaining higher payment terms are available to qualifying retailers",
                img: "https://cdn.faire.com/fastly/cccce764b65af9b1b6adfc0b8cb9fb9fe9818de5896c2f9fa31d57c605903825.png",
              },
            }),
      };
    }

    if (isRetailerEligibleForOwF && hasSkippedOwFUpsell) {
      return {
        title: strictLocalize(
          {
            defaultMessage:
              "You're eligible for up to {offeredMaxTermsMoney} in payment terms!",
            description: {
              text: "Header title letting retailer know they have payment terms",
              img: "https://cdn.faire.com/fastly/718645a8464ebb7e4ea7004fbbe645629bb5ca6db38391953ee82cb53e037d47.png",
            },
          },
          {
            offeredMaxTermsMoney: formattedOfferedMaxTerms,
          }
        ),
        subtitle: strictLocalize({
          defaultMessage:
            "Get everything you need for your new store—buy inventory on Faire and pay up to 60 days later, interest-free.",
          description: {
            text: "Header subtitle explaining payment terms to retailer",
            img: "https://cdn.faire.com/fastly/718645a8464ebb7e4ea7004fbbe645629bb5ca6db38391953ee82cb53e037d47.png",
          },
        }),
      };
    }

    return {
      title: strictLocalize({
        defaultMessage: "Congrats! You’ve got payment terms",
        description: {
          text: "Header title letting retailer know they have payment terms",
          img: "https://cdn.faire.com/fastly/ee58f6a470130806b26663ed1eaff1a92135ec4c6f5f4a425e46f4af13bd0206.png",
        },
      }),
      subtitle: strictLocalize({
        defaultMessage:
          "This means you can buy inventory today and pay up to 60 days later—interest-free.",
        description: {
          text: "Header subtitle explaining payment terms to retailer",
          img: "https://cdn.faire.com/fastly/ee58f6a470130806b26663ed1eaff1a92135ec4c6f5f4a425e46f4af13bd0206.png",
        },
      }),
    };
  }, [
    isUpmarketQualified,
    isRetailerEligibleForOwF,
    hasSkippedOwFUpsell,
    strictLocalize,
    retailer?.terms_limit,
    displayCurrency,
    isTreatmentForSccLimitIncreaseExperiment,
    formattedOfferedMaxTerms,
  ]);

  return <SignUpTitle title={title} subtitle={subtitle} />;
};
